import { useMemo, useState, useRef, useCallback, memo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import {
  MoreHorizontal,
  CheckCheck,
  Trash2,
  Maximize2,
  Minimize2,
  RotateCcw,
  Circle,
  CheckCircle2,
} from 'lucide-react'
import { HexColorPicker } from 'react-colorful'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AddTask } from './add-task'
import { SortableTodoItem } from './todo-item'
import { VirtualList } from './ui/virtual-list'
import { itemPresence, smoothSpring } from './motion-presets'
export function StatusSection({
  status,
  todos,
  onQuickAdd,
  selectedIds,
  editingId,
  displayStatus,
  color,
  onColorChange,
  onColorPickerOpenChange,
  recentlyDuplicatedIds,
  newTaskId,
  isSelecting,
  containerId,
  onUpdate,
  onDelete,
  draggingIds,
  isDragging,
  dragSizes,
  registerItem,
  showListBadge,
  addTaskInputRef,
  handleClearSelectedIds,
  className,
  uiSettings,
  onDuplicate,
  setEditingId,
  anyExpanded,
  scrollContainerRef,
  onToggle,
  onSelect,
  showHeader = true,
  ...itemProps
}) {
  const localDisplayStatus = status.charAt(0).toUpperCase() + status.slice(1)
  const [inlineAddAfterId, setInlineAddAfterId] = useState(null)

  // State for controlling popover and tooltip to prevent tooltip flashing on close
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const justClosedRef = useRef(false)

  // Use the passed color, fallback to defaults if for some reason missing (though store handles init)
  const effectiveColor = color || (status === 'done' ? '#22c55e' : '#64748b')
  const { setNodeRef } = useDroppable({
    id: `container:${containerId}`,
    data: {
      type: 'container',
      containerId,
    },
  })
  const todoIds = useMemo(() => todos.map((todo) => todo.id), [todos])
  const handlePickerOpenChange = useCallback(
    (open) => {
      setIsPickerOpen(open)
      onColorPickerOpenChange(open)
      if (open) {
        setIsTooltipOpen(false)
      } else {
        justClosedRef.current = true
        setTimeout(() => {
          justClosedRef.current = false
        }, 200)
      }
    },
    [onColorPickerOpenChange],
  )
  const handleTooltipOpenChange = useCallback(
    (open) => {
      if (isPickerOpen || justClosedRef.current) return
      setIsTooltipOpen(open)
    },
    [isPickerOpen],
  )
  const handleColorDotClick = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handleInlineInsert = useCallback(
    (id, position) => {
      if (position === 'top') {
        // Find index of id
        const index = todos.findIndex((t) => t.id === id)
        if (index === 0) {
          // If first, we just scroll to top AddTask or show one
          // For consistency, let's just use the top AddTask or show one here
          setInlineAddAfterId('top')
        } else {
          setInlineAddAfterId(todos[index - 1].id)
        }
      } else {
        setInlineAddAfterId(id)
      }
    },
    [todos],
  )
  const handleExpandAll = useCallback(() => {
    todos.forEach((t) => {
      if (!t.isExpanded) {
        onUpdate(
          t.id,
          {
            isExpanded: true,
          },
          { multi: true },
        )
      }
    })
  }, [todos, onUpdate])
  const handleCollapseAll = useCallback(() => {
    todos.forEach((t) => {
      if (t.isExpanded) {
        onUpdate(
          t.id,
          {
            isExpanded: false,
          },
          { multi: true },
        )
      }
    })
  }, [todos, onUpdate])
  const handleMarkAllDone = useCallback(() => {
    todos.forEach((t) => {
      if (!t.completed) {
        onUpdate(
          t.id,
          {
            status: 'done',
            completed: true,
          },
          { multi: true },
        )
      }
    })
  }, [todos, onUpdate])
  const handleMarkAllTodo = useCallback(() => {
    todos.forEach((t) => {
      if (t.completed) {
        onUpdate(
          t.id,
          {
            status: 'todo',
            completed: false,
          },
          { multi: true },
        )
      }
    })
  }, [todos, onUpdate])
  const handleDeleteAll = useCallback(() => {
    todos.forEach((t) => {
      onDelete(t.id)
    })
  }, [todos, onDelete])
  const handleInlineAddCancel = useCallback(() => {
    setInlineAddAfterId(null)
  }, [])
  return (
    <motion.div
      layout="position"
      layoutId={`status-section-${status}`}
      transition={smoothSpring}
      className={cn('flex min-h-0 flex-col', showHeader && 'space-y-2', className)}
    >
      {showHeader && (
        <StatusSectionHeader
          status={status}
          displayStatus={displayStatus || localDisplayStatus}
          effectiveColor={effectiveColor}
          isPickerOpen={isPickerOpen}
          isTooltipOpen={isTooltipOpen}
          handlePickerOpenChange={handlePickerOpenChange}
          handleTooltipOpenChange={handleTooltipOpenChange}
          handleColorDotClick={handleColorDotClick}
          onColorChange={onColorChange}
          todosCount={todos.length}
          handleExpandAll={handleExpandAll}
          handleCollapseAll={handleCollapseAll}
          handleMarkAllTodo={handleMarkAllTodo}
          handleMarkAllDone={handleMarkAllDone}
          handleDeleteAll={handleDeleteAll}
        />
      )}

      <StatusSectionTasks
        status={status}
        containerId={containerId}
        todoIds={todoIds}
        setNodeRef={setNodeRef}
        onQuickAdd={onQuickAdd}
        displayStatus={displayStatus || localDisplayStatus}
        isDragging={isDragging}
        isSelecting={isSelecting}
        addTaskInputRef={addTaskInputRef}
        inlineAddAfterId={inlineAddAfterId}
        handleInlineAddCancel={handleInlineAddCancel}
        todos={todos}
        selectedIds={selectedIds}
        editingId={editingId}
        recentlyDuplicatedIds={recentlyDuplicatedIds}
        newTaskId={newTaskId}
        draggingIds={draggingIds}
        dragSizes={dragSizes}
        registerItem={registerItem}
        showListBadge={showListBadge}
        onUpdate={onUpdate}
        onDelete={onDelete}
        setEditingId={setEditingId}
        onDuplicate={onDuplicate}
        uiSettings={uiSettings}
        handleInlineInsert={handleInlineInsert}
        handleClearSelectedIds={handleClearSelectedIds}
        scrollContainerRef={scrollContainerRef}
        onToggle={onToggle}
        onSelect={onSelect}
        {...itemProps}
      />
    </motion.div>
  )
}
const StatusColorPicker = ({
  effectiveColor,
  isPickerOpen,
  isTooltipOpen,
  handlePickerOpenChange,
  handleTooltipOpenChange,
  handleColorDotClick,
  onColorChange,
}) => {
  return (
    <Popover open={isPickerOpen} onOpenChange={handlePickerOpenChange} modal>
      <StatusColorPickerTrigger
        effectiveColor={effectiveColor}
        isTooltipOpen={isTooltipOpen}
        handleTooltipOpenChange={handleTooltipOpenChange}
        handleColorDotClick={handleColorDotClick}
      />

      <PopoverContent className="w-auto p-3" align="start" data-color-picker="true">
        <HexColorPicker color={effectiveColor} onChange={onColorChange} />
      </PopoverContent>
    </Popover>
  )
}
const StatusColorPickerTrigger = ({
  effectiveColor,
  isTooltipOpen,
  handleTooltipOpenChange,
  handleColorDotClick,
}) => (
  <Tooltip open={isTooltipOpen} onOpenChange={handleTooltipOpenChange}>
    <TooltipTrigger asChild>
      <PopoverTrigger asChild>
        <button
          className="h-3 w-3 rounded-full hover:opacity-80 transition-opacity cursor-pointer ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shadow-sm"
          style={{
            backgroundColor: effectiveColor,
          }}
          onClick={handleColorDotClick}
        />
      </PopoverTrigger>
    </TooltipTrigger>
    <TooltipContent>
      <p>Color</p>
    </TooltipContent>
  </Tooltip>
)
const StatusSectionMenu = ({
  status,
  handleExpandAll,
  handleCollapseAll,
  handleMarkAllTodo,
  handleMarkAllDone,
  handleDeleteAll,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <MoreHorizontal className="h-4 w-4 text-muted-foreground ml-auto cursor-pointer hover:text-foreground transition-colors" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuLabel>Actions</DropdownMenuLabel>
      <DropdownMenuItem onClick={handleExpandAll}>
        <Maximize2 className="mr-2 h-4 w-4" />
        Expand
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleCollapseAll}>
        <Minimize2 className="mr-2 h-4 w-4" />
        Collapse
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {status === 'done' ? (
        <DropdownMenuItem onClick={handleMarkAllTodo}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reopen
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem onClick={handleMarkAllDone}>
          <CheckCheck className="mr-2 h-4 w-4" />
          Complete
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={handleDeleteAll}
        className="text-destructive focus:text-destructive"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Purge
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)
function StatusSectionHeader({
  status,
  displayStatus,
  effectiveColor,
  isPickerOpen,
  isTooltipOpen,
  handlePickerOpenChange,
  handleTooltipOpenChange,
  handleColorDotClick,
  onColorChange,
  todosCount,
  handleExpandAll,
  handleCollapseAll,
  handleMarkAllTodo,
  handleMarkAllDone,
  handleDeleteAll,
}) {
  const Icon = status === 'done' ? CheckCircle2 : Circle
  return (
    <div className="flex items-center gap-2 mb-2">
      <StatusColorPicker
        effectiveColor={effectiveColor}
        isPickerOpen={isPickerOpen}
        isTooltipOpen={isTooltipOpen}
        handlePickerOpenChange={handlePickerOpenChange}
        handleTooltipOpenChange={handleTooltipOpenChange}
        handleColorDotClick={handleColorDotClick}
        onColorChange={onColorChange}
      />
      <div className="flex items-center gap-1.5 min-w-0">
        <Icon
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            status === 'done' ? 'text-emerald-500' : 'text-blue-500',
          )}
        />
        <h3 className={cn('font-semibold text-muted-foreground/80 tracking-tight', 'text-xs')}>
          {displayStatus}
        </h3>
      </div>
      <motion.span
        layout="position"
        transition={smoothSpring}
        className="text-muted-foreground/60 text-[11px] font-bold bg-muted/60 px-2 py-0.5 rounded-full shadow-sm"
      >
        {todosCount}
      </motion.span>
      <StatusSectionMenu
        status={status}
        handleExpandAll={handleExpandAll}
        handleCollapseAll={handleCollapseAll}
        handleMarkAllTodo={handleMarkAllTodo}
        handleMarkAllDone={handleMarkAllDone}
        handleDeleteAll={handleDeleteAll}
      />
    </div>
  )
}
function StatusSectionTasks({
  status,
  containerId,
  todoIds,
  setNodeRef,
  onQuickAdd,
  displayStatus,
  isDragging,
  isSelecting,
  addTaskInputRef,
  inlineAddAfterId,
  handleInlineAddCancel,
  todos,
  selectedIds,
  editingId,
  recentlyDuplicatedIds,
  newTaskId,
  draggingIds,
  dragSizes,
  registerItem,
  showListBadge,
  onUpdate,
  onDelete,
  handleInlineInsert,
  handleClearSelectedIds,
  anyExpanded,
  ...itemProps
}) {
  return (
    <SortableContext id={containerId} items={todoIds} strategy={verticalListSortingStrategy}>
      <SortableTodoList
        status={status}
        setNodeRef={setNodeRef}
        todos={todos}
        onQuickAdd={onQuickAdd}
        displayStatus={displayStatus}
        isDragging={isDragging}
        isSelecting={isSelecting}
        addTaskInputRef={addTaskInputRef}
        inlineAddAfterId={inlineAddAfterId}
        handleInlineAddCancel={handleInlineAddCancel}
        selectedIds={selectedIds}
        editingId={editingId}
        recentlyDuplicatedIds={recentlyDuplicatedIds}
        newTaskId={newTaskId}
        draggingIds={draggingIds}
        dragSizes={dragSizes}
        registerItem={registerItem}
        showListBadge={showListBadge}
        onUpdate={onUpdate}
        onDelete={onDelete}
        handleInlineInsert={handleInlineInsert}
        containerId={containerId}
        anyExpanded={anyExpanded}
        handleClearSelectedIds={handleClearSelectedIds}
        {...itemProps}
      />
    </SortableContext>
  )
}
const TopInlineAddTask = ({ onAdd, onCancel, status, disableInteractions, onInteract }) => {
  const handleAdd = useCallback(
    (text, overrides) => {
      onAdd(text, {
        ...overrides,
        index: 0,
      })
      onCancel()
    },
    [onAdd, onCancel],
  )
  return (
    <motion.div layout="position" {...itemPresence} className="mb-3">
      <UnifiedAddTask
        initialExpanded
        onCancel={onCancel}
        onAdd={handleAdd}
        status={status}
        disableInteractions={disableInteractions}
        onInteract={onInteract}
      />
    </motion.div>
  )
}
const ItemInlineAddTask = ({
  onAdd,
  onCancel,
  status,
  disableInteractions,
  insertAfterId,
  onInteract,
}) => {
  const handleAdd = useCallback(
    (text, overrides) => {
      onAdd(text, {
        ...overrides,
        insertAfterId,
      })
      onCancel()
    },
    [onAdd, insertAfterId, onCancel],
  )
  return (
    <motion.div layout="position" {...itemPresence} className="mb-3">
      <UnifiedAddTask
        initialExpanded
        onCancel={onCancel}
        onAdd={handleAdd}
        status={status}
        disableInteractions={disableInteractions}
        onInteract={onInteract}
      />
    </motion.div>
  )
}

// Rename AddTask inside this module or just use it.
const UnifiedAddTask = AddTask
// Memoize the SortableTodoRow to prevent re-renders of all items when something in itemData changes
// that this specific row doesn't care about.
const SortableTodoRow = memo(
  ({
    item: todo,
    index,
    inlineAddAfterId,
    onQuickAdd,
    handleInlineAddCancel,
    displayStatus,
    isDragging,
    isSelecting,
    selectedIds,
    editingId,
    recentlyDuplicatedIds,
    newTaskId,
    draggingIds,
    dragSizes,
    registerItem,
    showListBadge,
    onUpdate,
    onDelete,
    handleInlineInsert,
    handleClearSelectedIds,
    anyExpanded,
    itemProps,
  }) => {
    const lists = itemProps?.lists || []
    const list = lists.find((l) => l.id === todo.listId)
    const enabledFeatures = itemProps?.uiSettings?.enabledFeatures || {}

    return (
      <SortableTodoItem
        key={todo.id}
        todo={todo}
        list={list}
        enabledFeatures={enabledFeatures}
        isSelected={selectedIds.has(todo.id)}
        selectedIds={selectedIds}
        isEditing={editingId === todo.id}
        isNewlyDuplicated={recentlyDuplicatedIds.has(todo.id)}
        isNewTask={newTaskId === todo.id}
        draggingIds={draggingIds}
        isDragActive={isDragging}
        dragSizes={dragSizes}
        registerItem={registerItem}
        disableInteractions={isDragging || isSelecting}
        isSelecting={isSelecting}
        isFirst={index === 0}
        selectedCount={selectedIds.size}
        showListBadge={showListBadge}
        anyExpanded={anyExpanded}
        {...itemProps}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onInlineInsert={handleInlineInsert}
        onClearSelection={handleClearSelectedIds}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {inlineAddAfterId === todo.id && (
            <ItemInlineAddTask
              insertAfterId={todo.id}
              onAdd={onQuickAdd}
              onCancel={handleInlineAddCancel}
              status={displayStatus}
              disableInteractions={isDragging || isSelecting}
              onInteract={handleClearSelectedIds}
            />
          )}
        </AnimatePresence>
      </SortableTodoItem>
    )
  },
)
const SortableTodoList = ({
  status,
  setNodeRef,
  todos,
  onQuickAdd,
  displayStatus,
  isDragging,
  isSelecting,
  addTaskInputRef,
  inlineAddAfterId,
  handleInlineAddCancel,
  selectedIds,
  editingId,
  recentlyDuplicatedIds,
  newTaskId,
  draggingIds,
  dragSizes,
  registerItem,
  showListBadge,
  onUpdate,
  onDelete,
  handleInlineInsert,
  handleClearSelectedIds,
  anyExpanded,
  ...itemProps
}) => {
  const localListRef = useRef(null)
  const setRefs = useCallback(
    (node) => {
      setNodeRef(node)
      localListRef.current = node
    },
    [setNodeRef],
  )
  const resolvedScrollElement = itemProps?.scrollContainerRef || localListRef

  const handleQuickAddAtTop = useCallback(
    (text, overrides) => {
      onQuickAdd(text, { ...overrides, index: 0 })
    },
    [onQuickAdd],
  )
  const todoRowData = useMemo(
    () => ({
      inlineAddAfterId,
      onQuickAdd,
      handleInlineAddCancel,
      displayStatus,
      isDragging,
      isSelecting,
      selectedIds,
      editingId,
      recentlyDuplicatedIds,
      newTaskId,
      draggingIds,
      dragSizes,
      registerItem,
      showListBadge,
      onUpdate,
      onDelete,
      handleInlineInsert,
      handleClearSelectedIds,
      anyExpanded,
      itemProps,
    }),
    [
      displayStatus,
      dragSizes,
      draggingIds,
      editingId,
      handleInlineAddCancel,
      handleInlineInsert,
      inlineAddAfterId,
      isDragging,
      isSelecting,
      itemProps,
      newTaskId,
      onDelete,
      onQuickAdd,
      onUpdate,
      recentlyDuplicatedIds,
      registerItem,
      selectedIds,
      showListBadge,
      handleClearSelectedIds,
      anyExpanded,
    ],
  )
  const { isMobile } = itemProps
  return (
    <motion.div
      layout="position"
      transition={smoothSpring}
      ref={setRefs}
      className={cn('flex flex-1 flex-col gap-2 min-h-0 rounded-lg', isMobile ? 'p-2' : 'p-4')}
    >
      {status !== 'done' && (
        <UnifiedAddTask
          onAdd={handleQuickAddAtTop}
          status={displayStatus}
          disableInteractions={isDragging || isSelecting}
          inputRef={addTaskInputRef}
          onInteract={handleClearSelectedIds}
        />
      )}
      <AnimatePresence initial={false} mode="popLayout">
        {status !== 'done' && inlineAddAfterId === 'top' && (
          <TopInlineAddTask
            onAdd={onQuickAdd}
            onCancel={handleInlineAddCancel}
            status={displayStatus}
            disableInteractions={isDragging || isSelecting}
            onInteract={handleClearSelectedIds}
          />
        )}
      </AnimatePresence>
      <VirtualList
        items={todos}
        itemKey="id"
        itemRenderer={SortableTodoRow}
        itemData={todoRowData}
        estimatedItemHeight={112}
        itemGap={8}
        minItems={50}
        scrollElement={resolvedScrollElement}
        className="relative"
        contentClassName="relative"
      />
    </motion.div>
  )
}
